<template>
  <div class="fa-icon-picker-compact">
    <div v-if="label" class="ant-col-24 ant-form-item-label">
      <label :title="label" class="mb-2">{{ label }}</label>
    </div>

    <!-- Kompaktné zobrazenie vybranej ikony -->
    <div class="selected-icon-display" @click="openModal">
      <div v-if="selectedIcon" class="icon-preview">
        <i v-if="!selectedIcon.startsWith('lucide:')" :class="selectedIcon"></i>
        <component v-else :is="lucideComponent(selectedIcon)" :size="20" color="#1890ff" />
        <span class="ml-2">{{ selectedIcon }}</span>
      </div>
      <div v-else class="icon-placeholder">
        <i class="fas fa-icons"></i>
        <span class="ml-2">Kliknite pre výber ikony</span>
      </div>
      <a-button size="small" type="primary" class="ml-2">
        <i class="fas fa-search"></i>
      </a-button>
    </div>

    <!-- Modal s vyhľadávaním -->
    <a-modal
      v-model="modalVisible"
      title="Výber ikony"
      width="800px"
      :footer="null"
    >
      <div class="icon-source-switch mb-3">
        <a-radio-group v-model="iconSource" size="small" button-style="solid" @change="filterIcons">
          <a-radio-button value="all">Všetky</a-radio-button>
          <a-radio-button value="fa">FontAwesome</a-radio-button>
          <a-radio-button value="lucide">Lucide</a-radio-button>
        </a-radio-group>
      </div>
      <a-input-search
        v-model="searchQuery"
        placeholder="Vyhľadať ikonu (napr. home, user, calendar...)"
        class="mb-3"
        @input="filterIcons"
        size="large"
      />

      <div class="selected-icon-preview mb-3" v-if="tempSelectedIcon">
        <strong>Vybraná ikona:</strong>
        <div class="preview-box">
          <i v-if="!tempSelectedIcon.startsWith('lucide:')" :class="tempSelectedIcon"></i>
          <component v-else :is="lucideComponent(tempSelectedIcon)" :size="24" color="#1890ff" />
          <span class="ml-2">{{ tempSelectedIcon }}</span>
        </div>
      </div>

      <div class="icons-grid" v-if="filteredIcons.length > 0">
        <div
          v-for="(icon, index) in paginatedIcons"
          :key="icon + '_' + index"
          :class="['icon-item', { selected: tempSelectedIcon === icon }]"
          @click="selectTempIcon(icon)"
          :title="icon"
        >
          <i v-if="!icon.startsWith('lucide:')" :class="icon"></i>
          <component v-else :is="lucideComponent(icon)" :size="24" color="#595959" />
        </div>
      </div>

      <div v-else class="text-center text-muted p-4">
        Žiadne ikony nenájdené
      </div>

      <div class="text-center mt-3" v-if="totalPages > 1">
        <a-pagination
          v-model="currentPage"
          :total="filteredIcons.length"
          :pageSize="iconsPerPage"
          size="small"
          @change="onPageChange"
        />
      </div>

      <div class="text-right mt-3">
        <a-button @click="closeModal" class="mr-2">Zrušiť</a-button>
        <a-button type="primary" @click="confirmSelection" :disabled="!tempSelectedIcon">
          Potvrdiť výber
        </a-button>
      </div>
    </a-modal>
  </div>
</template>

<script>
import * as LucideIcons from 'lucide-vue';

// Zoznam Lucide ikon s prefixom
const lucideIconNames = Object.keys(LucideIcons).filter(k => typeof LucideIcons[k] === 'object' && LucideIcons[k].render).map(k => 'lucide:' + k);

export default {
  name: 'FontAwesomeIconPicker',
  props: {
    label: String,
    data: String,
    value: String
  },
  data() {
    return {
      selectedIcon: this.data || this.value || '',
      tempSelectedIcon: '',
      modalVisible: false,
      searchQuery: '',
      currentPage: 1,
      iconsPerPage: 100,
      iconSource: 'all',
      // FontAwesome 5 Free solid icons - kompletný zoznam
      allIcons: [
        // Základné
        'fas fa-home', 'fas fa-user', 'fas fa-users', 'fas fa-envelope', 'fas fa-phone', 'fas fa-calendar',
        'fas fa-search', 'fas fa-heart', 'fas fa-star', 'fas fa-check', 'fas fa-times', 'fas fa-icons',
        'fas fa-plus', 'fas fa-minus', 'fas fa-edit', 'fas fa-trash', 'fas fa-save', 'fas fa-ban',
        'fas fa-download', 'fas fa-upload', 'fas fa-print', 'fas fa-share', 'fas fa-link', 'fas fa-external-link-alt',

        // Médiá
        'fas fa-image', 'fas fa-images', 'fas fa-file', 'fas fa-folder', 'fas fa-camera', 'fas fa-video',
        'fas fa-music', 'fas fa-play', 'fas fa-pause', 'fas fa-stop', 'fas fa-forward', 'fas fa-film',
        'fas fa-backward', 'fas fa-volume-up', 'fas fa-volume-down', 'fas fa-volume-mute', 'fas fa-volume-off',
        'fas fa-microphone', 'fas fa-microphone-slash', 'fas fa-headphones', 'fas fa-photo-video',

        // Nastavenia
        'fas fa-cog', 'fas fa-cogs', 'fas fa-wrench', 'fas fa-tools', 'fas fa-sliders-h', 'fas fa-filter',
        'fas fa-lock', 'fas fa-unlock', 'fas fa-key', 'fas fa-shield-alt', 'fas fa-eye', 'fas fa-user-shield',
        'fas fa-eye-slash', 'fas fa-bell', 'fas fa-comment', 'fas fa-comments', 'fas fa-inbox', 'fas fa-envelope-open',

        // E-commerce
        'fas fa-shopping-cart', 'fas fa-shopping-bag', 'fas fa-shopping-basket', 'fas fa-credit-card',
        'fas fa-money-bill', 'fas fa-money-bill-wave', 'fas fa-wallet', 'fas fa-cash-register',
        'fas fa-tag', 'fas fa-tags', 'fas fa-barcode', 'fas fa-qrcode', 'fas fa-ticket-alt', 'fas fa-receipt',

        // Navigácia a mapy
        'fas fa-map', 'fas fa-map-marked', 'fas fa-map-marked-alt', 'fas fa-map-marker-alt', 'fas fa-map-pin',
        'fas fa-compass', 'fas fa-globe', 'fas fa-globe-europe', 'fas fa-globe-americas', 'fas fa-globe-asia',
        'fas fa-location-arrow', 'fas fa-directions', 'fas fa-route', 'fas fa-map-signs',

        // Doprava
        'fas fa-plane', 'fas fa-plane-arrival', 'fas fa-plane-departure', 'fas fa-car', 'fas fa-car-side',
        'fas fa-bus', 'fas fa-train', 'fas fa-bicycle', 'fas fa-motorcycle', 'fas fa-taxi', 'fas fa-truck',
        'fas fa-ship', 'fas fa-helicopter', 'fas fa-rocket', 'fas fa-subway', 'fas fa-tram',

        // Budovy
        'fas fa-building', 'fas fa-hospital', 'fas fa-university', 'fas fa-hotel', 'fas fa-store',
        'fas fa-warehouse', 'fas fa-church', 'fas fa-mosque', 'fas fa-synagogue', 'fas fa-home',
        'fas fa-house-damage', 'fas fa-city', 'fas fa-landmark', 'fas fa-monument',

        // Jedlo a nápoje
        'fas fa-utensils', 'fas fa-coffee', 'fas fa-beer', 'fas fa-wine-glass', 'fas fa-wine-bottle',
        'fas fa-pizza-slice', 'fas fa-hamburger', 'fas fa-ice-cream', 'fas fa-birthday-cake', 'fas fa-apple-alt',
        'fas fa-cookie', 'fas fa-cookie-bite', 'fas fa-bread-slice', 'fas fa-cheese', 'fas fa-drumstick-bite',
        'fas fa-egg', 'fas fa-fish', 'fas fa-lemon', 'fas fa-pepper-hot', 'fas fa-carrot',

        // Vzdelávanie
        'fas fa-book', 'fas fa-book-open', 'fas fa-bookmark', 'fas fa-graduation-cap', 'fas fa-pen',
        'fas fa-pencil-alt', 'fas fa-highlighter', 'fas fa-eraser', 'fas fa-paperclip', 'fas fa-clipboard',
        'fas fa-chalkboard', 'fas fa-chalkboard-teacher', 'fas fa-user-graduate', 'fas fa-school',

        // Grafy a dáta
        'fas fa-chart-bar', 'fas fa-chart-line', 'fas fa-chart-pie', 'fas fa-chart-area', 'fas fa-table',
        'fas fa-database', 'fas fa-server', 'fas fa-hdd', 'fas fa-project-diagram', 'fas fa-sitemap',

        // Technológie
        'fas fa-laptop', 'fas fa-desktop', 'fas fa-mobile-alt', 'fas fa-tablet-alt', 'fas fa-tv',
        'fas fa-keyboard', 'fas fa-mouse', 'fas fa-wifi', 'fas fa-bluetooth', 'fas fa-usb',
        'fas fa-plug', 'fas fa-battery-full', 'fas fa-battery-half', 'fas fa-battery-empty', 'fas fa-battery-quarter',
        'fas fa-power-off', 'fas fa-ethernet', 'fas fa-sd-card', 'fas fa-sim-card', 'fas fa-memory',

        // Počasie
        'fas fa-sun', 'fas fa-moon', 'fas fa-cloud', 'fas fa-cloud-rain', 'fas fa-cloud-sun',
        'fas fa-snowflake', 'fas fa-bolt', 'fas fa-fire', 'fas fa-water', 'fas fa-wind',
        'fas fa-temperature-high', 'fas fa-temperature-low', 'fas fa-smog', 'fas fa-rainbow',

        // Príroda
        'fas fa-tree', 'fas fa-leaf', 'fas fa-seedling', 'fas fa-paw', 'fas fa-dog', 'fas fa-cat',
        'fas fa-horse', 'fas fa-dove', 'fas fa-spider', 'fas fa-frog', 'fas fa-otter', 'fas fa-kiwi-bird',
        'fas fa-feather', 'fas fa-feather-alt', 'fas fa-mountain', 'fas fa-campground',

        // Emócie
        'fas fa-smile', 'fas fa-laugh', 'fas fa-grin', 'fas fa-meh', 'fas fa-frown', 'fas fa-smile-beam',
        'fas fa-angry', 'fas fa-sad-tear', 'fas fa-surprise', 'fas fa-kiss', 'fas fa-heart-broken',
        'fas fa-grin-hearts', 'fas fa-grin-tears', 'fas fa-tired', 'fas fa-dizzy',

        // Gestá
        'fas fa-thumbs-up', 'fas fa-thumbs-down', 'fas fa-hand-peace', 'fas fa-hand-rock',
        'fas fa-hand-paper', 'fas fa-hand-scissors', 'fas fa-hand-spock', 'fas fa-hand-pointer',
        'fas fa-hand-holding', 'fas fa-hand-holding-heart', 'fas fa-hands', 'fas fa-praying-hands',

        // Ocenenia
        'fas fa-trophy', 'fas fa-medal', 'fas fa-award', 'fas fa-gift', 'fas fa-crown',
        'fas fa-gem', 'fas fa-ring', 'fas fa-certificate', 'fas fa-ribbon',

        // Umenie
        'fas fa-palette', 'fas fa-paint-brush', 'fas fa-magic', 'fas fa-wand-magic', 'fas fa-hammer',
        'fas fa-screwdriver', 'fas fa-ruler', 'fas fa-cut', 'fas fa-drafting-compass', 'fas fa-pen-fancy',

        // Šport a aktivity
        'fas fa-hiking', 'fas fa-walking', 'fas fa-running', 'fas fa-biking', 'fas fa-skiing',
        'fas fa-snowboarding', 'fas fa-swimming-pool', 'fas fa-dumbbell', 'fas fa-volleyball-ball',
        'fas fa-football-ball', 'fas fa-basketball-ball', 'fas fa-baseball-ball', 'fas fa-golf-ball',
        'fas fa-bowling-ball', 'fas fa-table-tennis', 'fas fa-hockey-puck', 'fas fa-skating',

        // Outdoor
        'fas fa-tent', 'fas fa-caravan', 'fas fa-igloo', 'fas fa-fire-alt', 'fas fa-anchor',
        'fas fa-parachute-box', 'fas fa-umbrella-beach', 'fas fa-binoculars', 'fas fa-compass',

        // Zdravie
        'fas fa-ambulance', 'fas fa-wheelchair', 'fas fa-hospital-alt', 'fas fa-user-md', 'fas fa-user-nurse',
        'fas fa-stethoscope', 'fas fa-syringe', 'fas fa-pills', 'fas fa-prescription', 'fas fa-heartbeat',
        'fas fa-band-aid', 'fas fa-thermometer', 'fas fa-x-ray', 'fas fa-tooth', 'fas fa-smoking',

        // Ľudia
        'fas fa-baby', 'fas fa-child', 'fas fa-male', 'fas fa-female', 'fas fa-user-tie',
        'fas fa-user-secret', 'fas fa-user-ninja', 'fas fa-user-astronaut', 'fas fa-user-friends',

        // Práca
        'fas fa-briefcase', 'fas fa-suitcase', 'fas fa-box', 'fas fa-archive', 'fas fa-folder-open',
        'fas fa-file-alt', 'fas fa-file-pdf', 'fas fa-file-word', 'fas fa-file-excel', 'fas fa-file-powerpoint',
        'fas fa-file-image', 'fas fa-file-video', 'fas fa-file-audio', 'fas fa-file-code', 'fas fa-file-archive',

        // Šípky a navigácia
        'fas fa-arrow-up', 'fas fa-arrow-down', 'fas fa-arrow-left', 'fas fa-arrow-right',
        'fas fa-arrow-circle-up', 'fas fa-arrow-circle-down', 'fas fa-arrow-circle-left', 'fas fa-arrow-circle-right',
        'fas fa-chevron-up', 'fas fa-chevron-down', 'fas fa-chevron-left', 'fas fa-chevron-right',
        'fas fa-angle-up', 'fas fa-angle-down', 'fas fa-angle-left', 'fas fa-angle-right',
        'fas fa-angle-double-up', 'fas fa-angle-double-down', 'fas fa-angle-double-left', 'fas fa-angle-double-right',
        'fas fa-caret-up', 'fas fa-caret-down', 'fas fa-caret-left', 'fas fa-caret-right',
        'fas fa-long-arrow-alt-up', 'fas fa-long-arrow-alt-down', 'fas fa-long-arrow-alt-left', 'fas fa-long-arrow-alt-right',

        // Akcie
        'fas fa-sort', 'fas fa-sort-up', 'fas fa-sort-down', 'fas fa-random', 'fas fa-sync', 'fas fa-sync-alt',
        'fas fa-redo', 'fas fa-redo-alt', 'fas fa-undo', 'fas fa-undo-alt', 'fas fa-reply', 'fas fa-retweet',
        'fas fa-expand', 'fas fa-compress', 'fas fa-arrows-alt', 'fas fa-maximize', 'fas fa-minimize',
        'fas fa-exchange-alt', 'fas fa-recycle', 'fas fa-share-alt', 'fas fa-share-square',

        // Ikony stavu
        'fas fa-info-circle', 'fas fa-info', 'fas fa-question-circle', 'fas fa-question',
        'fas fa-exclamation-circle', 'fas fa-exclamation', 'fas fa-exclamation-triangle',
        'fas fa-check-circle', 'fas fa-check-square', 'fas fa-times-circle', 'fas fa-plus-circle',
        'fas fa-minus-circle', 'fas fa-dot-circle', 'fas fa-circle', 'fas fa-square', 'fas fa-spinner',

        // Kalendár a čas
        'fas fa-calendar-alt', 'fas fa-calendar-check', 'fas fa-calendar-times', 'fas fa-calendar-plus',
        'fas fa-calendar-minus', 'fas fa-calendar-day', 'fas fa-calendar-week', 'fas fa-clock',
        'fas fa-hourglass', 'fas fa-hourglass-start', 'fas fa-hourglass-half', 'fas fa-hourglass-end',
        'fas fa-stopwatch', 'fas fa-history', 'fas fa-alarm-clock',

        // Sociálne
        'fas fa-flag', 'fas fa-flag-checkered', 'fas fa-thumbtack', 'fas fa-bookmark', 'fas fa-star-half-alt',
        'fas fa-rss', 'fas fa-rss-square', 'fas fa-podcast', 'fas fa-at', 'fas fa-hashtag',

        // Rôzne
        'fas fa-tractor', 'fas fa-wheelchair', 'fas fa-wrench', 'fas fa-toolbox', 'fas fa-hammer',
        'fas fa-paint-roller', 'fas fa-brush', 'fas fa-broom', 'fas fa-toilet', 'fas fa-shower',
        'fas fa-bath', 'fas fa-bed', 'fas fa-couch', 'fas fa-chair', 'fas fa-door-open', 'fas fa-door-closed',
        'fas fa-lightbulb', 'fas fa-fan', 'fas fa-blender', 'fas fa-blender-phone'
      ],
      lucideIcons: lucideIconNames,
      filteredIcons: []
    };
  },
  computed: {
    combinedIcons() {
      if (this.iconSource === 'fa') return this.allIcons;
      if (this.iconSource === 'lucide') return this.lucideIcons;
      return [...this.allIcons, ...this.lucideIcons];
    },
    paginatedIcons() {
      const start = (this.currentPage - 1) * this.iconsPerPage;
      const end = start + this.iconsPerPage;
      return this.filteredIcons.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredIcons.length / this.iconsPerPage);
    }
  },
  mounted() {
    this.filteredIcons = [...this.allIcons];
  },
  watch: {
    data(newVal) {
      this.selectedIcon = newVal || '';
    },
    value(newVal) {
      this.selectedIcon = newVal || '';
    }
  },
  methods: {
    openModal() {
      this.tempSelectedIcon = this.selectedIcon;
      this.modalVisible = true;
      this.searchQuery = '';
      this.iconSource = 'all';
      this.filteredIcons = [...this.combinedIcons];
      this.currentPage = 1;
    },
    closeModal() {
      this.modalVisible = false;
      this.searchQuery = '';
      this.tempSelectedIcon = '';
    },
    filterIcons() {
      const source = this.combinedIcons;
      if (!this.searchQuery.trim()) {
        this.filteredIcons = [...source];
      } else {
        const query = this.searchQuery.toLowerCase();
        this.filteredIcons = source.filter(icon =>
          icon.toLowerCase().includes(query)
        );
      }
      this.currentPage = 1;
    },
    selectTempIcon(icon) {
      this.tempSelectedIcon = icon;
    },
    confirmSelection() {
      this.selectedIcon = this.tempSelectedIcon;
      this.$emit('change', this.selectedIcon);
      this.$emit('input', this.selectedIcon);
      this.closeModal();
    },
    onPageChange(page) {
      this.currentPage = page;
    },
    lucideComponent(icon) {
      const name = icon.replace('lucide:', '');
      return LucideIcons[name] || null;
    }
  }
};
</script>

<style scoped>
.fa-icon-picker-compact {
  width: 100%;
}

.icon-source-switch {
  display: flex;
  gap: 8px;
  align-items: center;
}

.selected-icon-display {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  transition: all 0.2s;
}

.selected-icon-display:hover {
  border-color: #1890ff;
  background: #f0f7ff;
}

.icon-preview {
  display: flex;
  align-items: center;
  flex: 1;
}

.icon-preview i {
  font-size: 20px;
  color: #1890ff;
}

.icon-placeholder {
  display: flex;
  align-items: center;
  flex: 1;
  color: #8c8c8c;
}

.icon-placeholder i {
  font-size: 18px;
}

.selected-icon-preview {
  padding: 10px;
  background: #f5f5f5;
  border-radius: 4px;
}

.preview-box {
  display: flex;
  align-items: center;
  font-size: 18px;
  margin-top: 5px;
}

.preview-box i {
  font-size: 24px;
  color: #1890ff;
}

.icons-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(60px, 1fr));
  gap: 8px;
  max-height: 400px;
  overflow-y: auto;
  padding: 10px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: #fafafa;
}

.icon-item {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  transition: all 0.2s;
  height: 60px;
}

.icon-item i {
  font-size: 24px;
  color: #595959;
}

.icon-item:hover {
  border-color: #1890ff;
  background: #e6f7ff;
  transform: scale(1.05);
}

.icon-item:hover i {
  color: #1890ff;
}

.icon-item.selected {
  border-color: #1890ff;
  background: #e6f7ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

.icon-item.selected i {
  color: #1890ff;
}
</style>
